import { ReactNode, useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useInactivityLogout } from "@/hooks/useInactivityLogout";
import { useSignupRecovery } from "@/hooks/useSignupRecovery";
import { useBackendSubscription } from "@/hooks/useBackendSubscription";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MobileBottomNav } from "./MobileBottomNav";
import { NativeBottomTabBar } from "./NativeBottomTabBar";
import { Capacitor } from "@capacitor/core";
import { SubscriptionGate } from "./SubscriptionGate";
import { SubscriptionGateErrorBoundary } from "./SubscriptionGateErrorBoundary";
import { PageErrorBoundary } from "./PageErrorBoundary";
import { isIOSNative } from "@/services/revenuecat";
import { isActiveWorkflow, isAppraisalRoute } from "@/lib/activeWorkflowGuard";
import { isPlatformPrivileged } from "@/lib/privilegedAccess";
import { useDemoAccess } from "@/hooks/useDemoAccess";
import { lockedUserMayRender } from "@/lib/billingAccess";

interface ProtectedRouteProps {
  children: ReactNode;
}

/** Check if stored auth tokens exist in localStorage */
const hasStoredTokens = (): boolean => {
  try {
    const raw = localStorage.getItem("sb-wfsbisepapnnnqyyekip-auth-token");
    return !!raw && raw.includes("refresh_token");
  } catch { return false; }
};

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, session } = useAuth();
  const backendSub = useBackendSubscription();
  const demoAccess = useDemoAccess();
  const [showContactAdmin, setShowContactAdmin] = useState(false);

  const isDashboard = location.pathname === "/";
  const isIOS = isIOSNative();
  const isNativePlatform = Capacitor.isNativePlatform();

  // Run signup recovery check for broken onboarding state
  useSignupRecovery(session);
  useInactivityLogout();

  // Redirect unauthenticated users
  // WORKFLOW GUARD: If an active appraisal exists OR we're on an appraisal route
  // with stored tokens, do NOT redirect — the session will restore.
  useEffect(() => {
    if (status === "unauthed") {
      // On native: check for stored tokens AND active workflow
      if (isNativePlatform) {
        if (hasStoredTokens()) {
          logger.warn("[ProtectedRoute] Status=unauthed but stored tokens found on native — NOT redirecting to /auth");
          return;
        }
      }
      // On any platform: if there's an active appraisal workflow, don't redirect
      if (isActiveWorkflow()) {
        logger.warn("[ProtectedRoute] Status=unauthed but active workflow detected — NOT redirecting to /auth");
        return;
      }
      navigate("/auth", { replace: true });
    }
  }, [status, navigate, isNativePlatform]);

  // ── Web/Android subscription enforcement (iOS uses SubscriptionGate) ──
  // WORKFLOW GUARD: During active appraisals, transient subscription failures
  // must never redirect or block the driver.
  useEffect(() => {
    if (status !== "authed" || !session || isIOS) return;
    if (backendSub.loading) return;

    const user = session.user;

    // Privileged bypass — platform owners skip subscription checks
    if (isPlatformPrivileged(user.email)) {
      setShowContactAdmin(false);
      return;
    }

    // ── ACCESS GRANTED (including preserved prior access during transient errors) ──
    if (backendSub.accessAllowed) {
      setShowContactAdmin(false);
      return;
    }

    // ── DEMO COMPANY: exploring the isolated demo tenant needs no plan ──
    // Verified against the database, and RLS still confines the user to the
    // companies they belong to, so their own company stays gated.
    if (demoAccess.loading || demoAccess.isDemoContext) {
      setShowContactAdmin(false);
      return;
    }

    // ── WORKFLOW PROTECTION: Never interrupt an active appraisal ──
    if (isActiveWorkflow() || isAppraisalRoute(location.pathname)) {
      logger.warn("[ProtectedRoute] Subscription check failed but active workflow detected — preserving route");
      setShowContactAdmin(false);
      // Retry in background
      void backendSub.refresh();
      return;
    }

    // Drivers are especially sensitive to transient access state drift.
    // Keep job routes usable, but do not leave dashboard access open.
    if (backendSub.role === "driver") {
      if (backendSub.error || backendSub.accessSource === "none") {
        logger.warn("[ProtectedRoute] Driver access unresolved due to backend error; preserving route and retrying");
        setShowContactAdmin(false);
        void backendSub.refresh();
        return;
      }

      if (location.pathname.startsWith("/driver") || location.pathname.startsWith("/job/") || location.pathname.startsWith("/resume-job")) {
        logger.warn("[ProtectedRoute] Driver subscription denied but on active job route — allowing continued access");
        setShowContactAdmin(false);
        return;
      }

      setShowContactAdmin(true);
      return;
    }

    // ── BILLING MANAGEMENT: locked owners may still reach the billing page ──
    if (lockedUserMayRender(location.pathname, backendSub.role)) {
      setShowContactAdmin(false);
      return;
    }

    // Owner/standalone user with no access → redirect to subscribe
    toast.error("No active subscription found. Please subscribe to continue.");
    navigate("/subscribe", { replace: true });
  }, [status, session, isIOS, backendSub.loading, backendSub.accessAllowed, backendSub.accessSource, backendSub.role, navigate, location.pathname, demoAccess.loading, demoAccess.isDemoContext]);

  // ── Render ────────────────────────────────────────────────

  // Contain page-level render crashes so they can never loop the shell.
  const guardedChildren = <PageErrorBoundary routeKey={location.pathname}>{children}</PageErrorBoundary>;

  logger.debug("[ProtectedRoute] platform:", isNativePlatform ? Capacitor.getPlatform() : "web", "auth:", status, "access:", backendSub.accessAllowed, "activeWorkflow:", isActiveWorkflow());

  // ── iOS: IMMEDIATELY render SubscriptionGate — BEFORE any loading check ──
  if (isIOS && status === "authed" && session) {
    logger.debug("[ProtectedRoute] iOS → SubscriptionGate");
    const iosContent = (
      <>
        <div className="native-dock-pad">
          {guardedChildren}
        </div>
        <NativeBottomTabBar />
      </>
    );
    return (
      <SubscriptionGateErrorBoundary fallback={iosContent}>
        <SubscriptionGate onNoWebSubscription={() => navigate("/subscribe")}>
          {iosContent}
        </SubscriptionGate>
      </SubscriptionGateErrorBoundary>
    );
  }

  // ── Auth loading ──
  // On native WebView restarts (e.g. after camera kills WebView), auth takes
  // up to 8s to restore. If we have a valid subscription cache, stored tokens,
  // OR an active workflow, skip the spinner — render content immediately.
  if (status === "loading") {
    const shouldSkipSpinner = (
      (isNativePlatform && backendSub.accessAllowed) ||
      (isNativePlatform && hasStoredTokens() && isActiveWorkflow()) ||
      (isNativePlatform && hasStoredTokens() && isAppraisalRoute(location.pathname))
    );

    if (shouldSkipSpinner) {
      logger.debug("[ProtectedRoute] Auth loading but workflow/cache active — rendering content");
    } else {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Checking session…</p>
        </div>
      );
    }
  }

  // ── Unauthed render: show spinner while redirect happens ──
  // But if active workflow exists, render content instead (auth restoring)
  if (status === "unauthed") {
    if (isActiveWorkflow() || (isNativePlatform && hasStoredTokens())) {
      logger.debug("[ProtectedRoute] Unauthed but workflow active — rendering content while auth restores");
    } else {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Redirecting…</p>
        </div>
      );
    }
  }

  // ── iOS fallback ──
  if (isIOS) {
    return (
      <>
        <div className="native-dock-pad">
          {guardedChildren}
        </div>
        <NativeBottomTabBar />
      </>
    );
  }

  const shouldForceSubscribe = (
    status === "authed" &&
    !!session &&
    !backendSub.loading &&
    !backendSub.accessAllowed &&
    backendSub.role !== "driver" &&
    !isActiveWorkflow() &&
    !isAppraisalRoute(location.pathname) &&
    !lockedUserMayRender(location.pathname, backendSub.role) &&
    !isPlatformPrivileged(session.user.email) &&
    // Exploring the isolated demo tenant is allowed without a plan.
    !demoAccess.loading && !demoAccess.isDemoContext
  );

  if (shouldForceSubscribe) {
    return <Navigate to="/subscribe" replace />;
  }

  // ── Web/Android: subscription loading ──
  const content = (
    <>
      <div className={isDashboard ? "" : "pb-16 sm:pb-0"}>
        {guardedChildren}
      </div>
      {!isDashboard && <MobileBottomNav />}
    </>
  );

  // Privileged bypass — render immediately even while sub is loading
  if (isPlatformPrivileged(session?.user?.email)) {
    return content;
  }

  // ── Web/Android: subscription loading ──
  // CRITICAL: If user was previously verified (accessAllowed=true) OR is in an
  // active workflow, do NOT show the loading spinner.
  if (backendSub.loading && !backendSub.accessAllowed && !isActiveWorkflow()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Verifying subscription…</p>
      </div>
    );
  }

  // ── Driver with no inherited subscription — show "contact admin" ──
  if (showContactAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center">
        <div className="rounded-full bg-muted p-4">
          <Loader2 className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">No Active Subscription</h2>
        <p className="text-muted-foreground max-w-md">
          Your company's subscription is not currently active. Please contact your administrator to restore access.
        </p>
        <button
          onClick={() => navigate("/auth")}
          className="mt-4 text-sm text-primary underline hover:text-primary/80"
        >
          Back to login
        </button>
      </div>
    );
  }

  return content;
};
